/**
 * GLSL code for rendering hatch patterns.
 * @module
 */
import stableHash from "common/utils/stableHash";
import type { Vec2 } from "geom/vec2";
import type { IUniform } from "three";
import { Color, NormalBlending, ShaderMaterial, UniformsUtils, Vector2 } from "three";
import { defaultStencilProperties } from "../defaultStencilProperties";
import type { HighlightDataTexture } from "../HighlightDataTexture";
import {
  entityIdHighlightDeclarationsFragment,
  entityIdHighlightDeclarationsVertex,
  entityIdHighlightFragment,
  entityIdHighlightVertexNoMix,
} from "./shaders/entityIdHighlight";

const uniforms = {
  hatchDirection: { type: "v2", value: new Vector2(1.0, 0) },
  lineSpacing: { type: "f", value: 4.0 },
  lineThickness: { type: "f", value: 0.5 },
  color: { type: "c", value: new Color(0.0, 0.0, 0.0) },
  highlightedIds: { type: "t", value: null },
};

export const hatchDistanceScaleFunction = /* glsl */ `
float getHatchDistanceScale(vec2 xy, vec2 xyScale, float minScale) {
  // The math here isn't precise when accounting for perspective transforms.
  float dx = dFdx(xy.x) * xyScale.x;
  float dy = dFdy(xy.y) * xyScale.y;

  // Threshold to keep a minimum absolute width.
  return max(max(dx, dy), minScale);
}
`;

const hatchVertexShader = /* glsl */ `
varying vec3 vPos;
 
${entityIdHighlightDeclarationsVertex}

void main() {
  mat4 viewWithoutTranslation = mat4(mat3(viewMatrix));
  vPos = (viewWithoutTranslation * modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

  ${entityIdHighlightVertexNoMix}
}
`;

const hatchFragmentShaderTemplate = /* glsl */ `
varying vec3 vPos;

${entityIdHighlightDeclarationsFragment}
${hatchDistanceScaleFunction}

uniform vec2 hatchDirection;
uniform float lineSpacing;
uniform float lineThickness;
uniform vec3 color;

void main() {
  float distance = distanceFunction(normalize(hatchDirection), vPos.xy, lineSpacing);

  vec2 deltaScale = vec2(1.0 / lineThickness);
  float minDelta = 1.0;
  float delta = getHatchDistanceScale(vPos.xy, deltaScale, minDelta);

  if (distance > lineThickness * delta) {
    discard;
  }

  gl_FragColor = vec4(color, 1.);

  ${entityIdHighlightFragment}
}
`;

const hatchDistanceFunction = /* glsl */ `
float distanceFunction(vec2 lineDirection, vec2 point, float interval) {
  float distance = length(point - dot(lineDirection, point) * lineDirection);
  return mod(distance, interval);
}
`;

const crossHatchDistanceFunction = /* glsl */ `
float distanceFunction(vec2 lineDirection, vec2 point, float interval) {
  float distance = length(point - dot(lineDirection, point) * lineDirection);
  vec2 crossLineDirection = vec2(lineDirection.y, -lineDirection.x);
  float crossDistance = length(point - dot(crossLineDirection, point) * crossLineDirection);

  return min(mod(distance, interval), mod(crossDistance, interval));
}
`;

const HatchShader = {
  vertexShader: hatchVertexShader,
  fragmentShader: `${hatchDistanceFunction}\n${hatchFragmentShaderTemplate}`,
  uniforms: uniforms,
} as const;

const CrossHatchShader = {
  vertexShader: hatchVertexShader,
  fragmentShader: `${crossHatchDistanceFunction}\n${hatchFragmentShaderTemplate}`,
  uniforms: uniforms,
} as const;

export interface HatchMaterialOptions {
  readonly lineSpacing: number;
  readonly hatchDirection: Vec2;
  readonly lineThickness: number;
  readonly color: Color | number | string;
  readonly crossHatch: boolean;
}

export class HatchMaterialFactory {
  private materialCache: { [key: string]: ShaderMaterial } = {};

  private static _instance: HatchMaterialFactory | undefined;

  static get instance(): HatchMaterialFactory {
    return this._instance ?? (this._instance = new HatchMaterialFactory());
  }

  private highlightDataTextureUniform: IUniform = { value: null };
  private isolateModeUniform: IUniform = { value: false };

  private constructor() {}

  getMaterial(options: Partial<HatchMaterialOptions>): ShaderMaterial {
    const hash = stableHash(options);
    if (this.materialCache[hash]) {
      return this.materialCache[hash];
    }

    const { crossHatch, color, ...uniforms } = options;

    const hatchMaterial = new ShaderMaterial({
      ...defaultStencilProperties,
      defines: {},
      fragmentShader: crossHatch ? CrossHatchShader.fragmentShader : HatchShader.fragmentShader,
      vertexShader: crossHatch ? CrossHatchShader.vertexShader : HatchShader.vertexShader,
      uniforms: UniformsUtils.clone(HatchShader.uniforms),
      blending: NormalBlending,
      depthTest: true,
      depthWrite: false,
      transparent: true,
    });

    if (color) {
      hatchMaterial.uniforms.color.value = color instanceof Color ? color : new Color(color);
    }

    Object.keys(uniforms).forEach((key) => {
      hatchMaterial.uniforms[key].value = uniforms[key];
    });
    hatchMaterial.uniforms.highlightedIds = this.highlightDataTextureUniform;
    hatchMaterial.uniforms.isolateMode = this.isolateModeUniform;

    this.materialCache[hash] = hatchMaterial;
    return hatchMaterial;
  }

  prepareRender(highlightDataTexture?: HighlightDataTexture) {
    this.highlightDataTextureUniform.value = highlightDataTexture?.texture ?? null;
    this.isolateModeUniform.value = highlightDataTexture?.isolateMode ?? false;
  }
}
